import { Injectable, signal } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { MenuSectionType } from "../../types/menu-tree.type";
import { APP_PATHS } from "../../shared/utils/app-route-paths";

export interface SidebarPanelView {
  panelOpen: boolean;
  panelPinned: boolean;
  activeSection: string | null;
  sectionIcon: string | null;
  sectionLabel: string | null;
  canToggle: boolean;
  pageLabel: string | null;
  pageIcon: string | null;
  pageSubtitle: string | null;
  pageCount: number | null;
}

interface RoutePageMeta {
  titleKey: string;
  subtitleKey?: string;
  icon?: string;
}

const ROUTE_PAGE_META: Record<string, RoutePageMeta> = {
  [APP_PATHS.messaging.contacts]: {
    titleKey: "CONTACT.PAGE.TITLE",
    subtitleKey: "CONTACT.PAGE.DESCRIPTION",
    icon: "address-book",
  },
  [APP_PATHS.messaging.lists]: {
    titleKey: "CONTACT_LIST.PAGE.TITLE", subtitleKey: "CONTACT_LIST.PAGE.DESCRIPTION", icon: "address-book",
  },
  [APP_PATHS.messaging.segments]: {
    titleKey: "CONTACT_SEGMENT.PAGE.TITLE", subtitleKey: "CONTACT_SEGMENT.PAGE.DESCRIPTION", icon: "address-book",
  },
  [APP_PATHS.messaging.inbox]: {
    titleKey: "MENU.TEAM_INBOX",
    icon: "inbox",
  },
};

@Injectable({ providedIn: "root" })
export class SidebarPanelService {
  /** Phones: the navigation drawer opened from the header menu button. */
  readonly mobileOpen = signal(false);

  openMobile(): void {
    this.mobileOpen.set(true);
  }

  closeMobile(): void {
    this.mobileOpen.set(false);
  }

  toggleMobile(): void {
    this.mobileOpen.update((open) => !open);
  }

  private visibleSections: MenuSectionType[] = [];
  private readonly viewSubject = new BehaviorSubject<SidebarPanelView>({
    panelOpen: false,
    panelPinned: false,
    activeSection: null,
    sectionIcon: null,
    sectionLabel: null,
    canToggle: false,
    pageLabel: null,
    pageIcon: null,
    pageSubtitle: null,
    pageCount: null,
  });

  readonly view$ = this.viewSubject.asObservable();

  panelOpen = false;
  panelPinned = false;
  activeSection: string | null = null;
  pageLabel: string | null = null;
  pageIcon: string | null = null;
  pageSubtitle: string | null = null;
  pageCount: number | null = null;

  setVisibleSections(sections: MenuSectionType[]) {
    this.visibleSections = sections;
    this.emitView();
  }

  getActiveSection(): MenuSectionType | null {
    if (!this.activeSection) {
      return null;
    }
    return (
      this.visibleSections.find((s) => s.section === this.activeSection) || null
    );
  }

  canToggleSubPanel(): boolean {
    return this.visibleSections.length > 0;
  }

  togglePanel() {
    if (!this.canToggleSubPanel()) {
      return;
    }
    if (this.panelOpen) {
      this.closeAndUnpin();
    } else {
      this.openAndPin();
    }
  }

  openAndPin() {
    this.panelPinned = true;
    this.panelOpen = true;
    this.emitView();
  }

  closeAndUnpin() {
    this.panelPinned = false;
    this.panelOpen = false;
    this.emitView();
  }

  openPanel() {
    this.panelOpen = true;
    this.emitView();
  }

  closePanel() {
    this.panelOpen = false;
    this.emitView();
  }

  setActiveSection(
    sectionKey: string | null,
    options?: { openPanel?: boolean; closePanel?: boolean },
  ) {
    this.activeSection = sectionKey;
    if (options?.closePanel) {
      this.panelOpen = false;
      this.panelPinned = false;
    } else if (options?.openPanel && this.canToggleSubPanel()) {
      this.panelOpen = true;
    }
    this.emitView();
  }

  updatePageFromRoute(
    path: string,
    match: { section: string; menu: string | null },
  ) {
    const normalizedPath = path.split("?")[0];
    this.pageCount = null;

    const staticMeta = ROUTE_PAGE_META[normalizedPath];
    if (staticMeta) {
      this.pageLabel = staticMeta.titleKey;
      this.pageSubtitle = staticMeta.subtitleKey ?? null;
      this.pageIcon = staticMeta.icon ?? null;
      this.emitView();
      return;
    }

    const section = this.visibleSections.find(
      (s) => s.section === match.section,
    );
    if (!section) {
      this.clearPageMeta();
      return;
    }

    if (match.menu) {
      const menu = section.items.find((item) => item.menu === match.menu);
      if (menu?.label) {
        this.pageLabel = menu.label;
        this.pageIcon = menu.icon ?? null;
        this.pageSubtitle = null;
        this.emitView();
        return;
      }
    }

    if (section.directLink && normalizedPath.startsWith(section.directLink)) {
      this.pageLabel = section.label;
      this.pageIcon = section.icon;
      this.pageSubtitle = null;
      this.emitView();
      return;
    }

    this.clearPageMeta();
  }

  setPageCount(count: number | null) {
    this.pageCount = count;
    this.emitView();
  }

  clearPageMeta() {
    this.pageLabel = null;
    this.pageIcon = null;
    this.pageSubtitle = null;
    this.pageCount = null;
    this.emitView();
  }

  restore(): string[] {
    let collapsedMenus: string[] = [];
    try {
      const state = localStorage.getItem("sidebar_state");
      if (!state) {
        return collapsedMenus;
      }
      const parsed = JSON.parse(state);
      this.panelPinned = parsed.panelPinned ?? false;
      // An open panel is only restored when the user pinned it with the
      // expand button; otherwise it always starts collapsed.
      this.panelOpen = this.panelPinned && (parsed.panelOpen ?? false);
      this.activeSection = parsed.activeSection || null;
      collapsedMenus = Array.isArray(parsed.collapsedMenus)
        ? parsed.collapsedMenus
        : [];

      const legacySectionMap: Record<string, string> = {
        inbox: "messaging",
      };
      if (this.activeSection && legacySectionMap[this.activeSection]) {
        this.activeSection = legacySectionMap[this.activeSection];
      }

      if (
        this.activeSection &&
        !this.visibleSections.some((s) => s.section === this.activeSection)
      ) {
        this.activeSection = null;
        this.panelOpen = false;
        this.panelPinned = false;
      }
    } catch {
      // Ignore
    }
    this.emitView();
    return collapsedMenus;
  }

  persist(collapsedMenus?: string[]) {
    try {
      let storedCollapsedMenus: string[] = [];
      if (collapsedMenus === undefined) {
        const state = localStorage.getItem("sidebar_state");
        if (state) {
          const stored = JSON.parse(state).collapsedMenus;
          storedCollapsedMenus = Array.isArray(stored) ? stored : [];
        }
      }

      localStorage.setItem(
        "sidebar_state",
        JSON.stringify({
          panelOpen: this.panelOpen,
          panelPinned: this.panelPinned,
          activeSection: this.activeSection,
          collapsedMenus: collapsedMenus ?? storedCollapsedMenus,
        }),
      );
    } catch {
      // Ignore
    }
  }

  private emitView() {
    const section = this.getActiveSection();
    this.viewSubject.next({
      panelOpen: this.panelOpen,
      panelPinned: this.panelPinned,
      activeSection: this.activeSection,
      sectionIcon: section?.icon ?? null,
      sectionLabel: section?.label ?? null,
      canToggle: this.canToggleSubPanel(),
      pageLabel: this.pageLabel,
      pageIcon: this.pageIcon,
      pageSubtitle: this.pageSubtitle,
      pageCount: this.pageCount,
    });
  }
}
